import React from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";

interface ReviewWarningsDialogProps {
  open: boolean;
  onClose: () => void;
  syntaxErrorsCount: number;
  invalidConfigsCount: number;
  invalidConfigRules?: { source: string; error: string }[];
  packagesMissingCount: number;
}

const ReviewWarningsDialog: React.FC<ReviewWarningsDialogProps> = ({
  open,
  onClose,
  syntaxErrorsCount,
  invalidConfigsCount,
  invalidConfigRules = [],
  packagesMissingCount,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: "#1b1a19", color: "#ffffff", borderBottom: "1px solid #3b3a39" }}>
        <Typography variant="inherit" fontWeight="bold" color="#ffffff">
          Fix issues before reviewing
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: "#1b1a19", pt: 3 }}>
        <Typography variant="body2" sx={{ color: "#c8c6c4", mb: 2 }}>
          This Draft has issues that must be resolved before you can review and submit:
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {(syntaxErrorsCount > 0 || packagesMissingCount > 0) && (
            <Box>
              <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 0.5 }}>
                Applicability Rules
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 3, listStyle: "disc", display: "flex", flexDirection: "column", gap: 0.5 }}>
                {syntaxErrorsCount > 0 && (
                  <Box component="li" sx={{ color: "#d5878b" }}>
                    <Typography variant="body2">
                      <strong>{syntaxErrorsCount}</strong> rule{syntaxErrorsCount === 1 ? "" : "s"} with syntax errors
                    </Typography>
                  </Box>
                )}
                {packagesMissingCount > 0 && (
                  <Box component="li" sx={{ color: "#d5878b" }}>
                    <Typography variant="body2">
                      <strong>{packagesMissingCount}</strong> package{packagesMissingCount === 1 ? "" : "s"} not in Library
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
          {invalidConfigsCount > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 0.5 }}>
                System Configurations
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 3, listStyle: "disc", display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box component="li" sx={{ color: "#d5878b" }}>
                  <Typography variant="body2">
                    <strong>{invalidConfigsCount}</strong> invalid configuration{invalidConfigsCount === 1 ? "" : "s"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
          {invalidConfigRules.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ color: "#ffffff", fontWeight: 600, mb: 0.5 }}>
                Package and Client Configurations
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 3, listStyle: "disc", display: "flex", flexDirection: "column", gap: 0.5 }}>
                {invalidConfigRules.map((entry, index) => (
                  <Box component="li" key={`${entry.source}-${index}`} sx={{ color: "#d5878b" }}>
                    <Typography variant="body2">
                      <strong>{entry.source}</strong>: {entry.error}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#1b1a19", padding: 2, borderTop: "1px solid #3b3a39" }}>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewWarningsDialog;
